/**
 * Reusable analytics widget — used by:
 *   • Partner Portal Dashboard (calls /partner-portal/analytics)
 *   • Admin Partner API Console (calls /partner-api/analytics with optional partner_id)
 *
 * Renders bucket selector (day/week/month), date range, duration breakdown
 * cards (1M/3M/6M/12M/other), a time-series table, and a plan-level table.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, RefreshCw, TrendingUp, IndianRupee, Users } from 'lucide-react';

const RS = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const DURATION_LABEL = { '1M': '1 Month', '3M': '3 Months', '6M': '6 Months', '12M': '12 Months', other: 'Other' };
const DURATION_TONE = {
  '1M': 'bg-emerald-50 border-emerald-200 text-emerald-800',
  '3M': 'bg-sky-50 border-sky-200 text-sky-800',
  '6M': 'bg-indigo-50 border-indigo-200 text-indigo-800',
  '12M': 'bg-purple-50 border-purple-200 text-purple-800',
  other: 'bg-slate-50 border-slate-200 text-slate-700',
};

export default function PartnerAnalyticsPanel({ endpoint, authHeaders, showPartnerSelector, partners }) {
  const [bucket, setBucket] = useState('day');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [partnerId, setPartnerId] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ bucket });
      if (dateFrom) params.append('from', dateFrom);
      if (dateTo) params.append('to', dateTo);
      if (showPartnerSelector && partnerId && partnerId !== 'all') params.append('partner_id', partnerId);
      const r = await axios.get(`${endpoint}?${params.toString()}`, { headers: authHeaders });
      setData(r.data);
    } finally {
      setLoading(false);
    }
  }, [endpoint, authHeaders, bucket, dateFrom, dateTo, partnerId, showPartnerSelector]);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="analytics-panel">
      {/* Filter bar */}
      <Card>
        <CardContent className="py-3 flex items-end gap-3 flex-wrap">
          <div>
            <Label className="text-xs">Bucket</Label>
            <Select value={bucket} onValueChange={setBucket}>
              <SelectTrigger className="w-[120px]" data-testid="analytics-bucket">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Day</SelectItem>
                <SelectItem value="week">Week</SelectItem>
                <SelectItem value="month">Month</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={dateFrom}
                   onChange={(e) => setDateFrom(e.target.value)}
                   className="w-[150px]" data-testid="analytics-from" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={dateTo}
                   onChange={(e) => setDateTo(e.target.value)}
                   className="w-[150px]" data-testid="analytics-to" />
          </div>
          {showPartnerSelector && (
            <div>
              <Label className="text-xs">Partner</Label>
              <Select value={partnerId} onValueChange={setPartnerId}>
                <SelectTrigger className="w-[200px]" data-testid="analytics-partner">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Partners</SelectItem>
                  {(partners || []).map((p) => (
                    <SelectItem key={p.partner_id} value={p.partner_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button variant="outline" size="icon" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
          <div className="ml-auto text-xs text-slate-500">
            {data.date_from?.slice(0, 10)} → {data.date_to?.slice(0, 10)}
          </div>
        </CardContent>
      </Card>

      {/* Top totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryTile icon={TrendingUp} label="Activations" value={data.totals.activations} testId="an-activations" />
        <SummaryTile label="Successful" value={data.totals.success} tone="emerald" testId="an-success" />
        <SummaryTile label="Failed" value={data.totals.failed} tone="red" testId="an-failed" />
        <SummaryTile icon={IndianRupee} label="Revenue" value={RS(data.totals.revenue)} tone="indigo" testId="an-revenue" />
        <SummaryTile icon={Users} label="Active Customers" value={data.totals.active_customers} tone="indigo" testId="an-customers" />
      </div>

      {/* Duration breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">By Plan Duration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {['1M', '3M', '6M', '12M', 'other'].map((k) => {
              const v = data.duration_totals?.[k] || { count: 0, success: 0, failed: 0, revenue: 0 };
              return (
                <div key={k} className={`rounded-md border p-3 ${DURATION_TONE[k]}`} data-testid={`an-duration-${k}`}>
                  <p className="text-xs font-semibold">{DURATION_LABEL[k]}</p>
                  <p className="text-2xl font-bold mt-1">{v.count}</p>
                  <div className="text-[11px] mt-1 space-y-0.5">
                    <p>✓ {v.success} · ✗ {v.failed}</p>
                    <p>Revenue: {RS(v.revenue)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Time series */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Trend ({data.bucket})</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600">
              <tr>
                <th className="text-left p-2">{data.bucket === 'day' ? 'Date' : data.bucket === 'week' ? 'Week' : 'Month'}</th>
                <th className="text-right p-2">1M</th>
                <th className="text-right p-2">3M</th>
                <th className="text-right p-2">6M</th>
                <th className="text-right p-2">12M</th>
                <th className="text-right p-2">Total</th>
                <th className="text-right p-2">Success</th>
                <th className="text-right p-2">Failed</th>
                <th className="text-right p-2">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {data.series.length === 0 ? (
                <tr><td colSpan="9" className="text-center py-6 text-slate-400">No data in range</td></tr>
              ) : data.series.map((r) => (
                <tr key={r.label} className="border-t hover:bg-slate-50">
                  <td className="p-2 font-mono text-xs">{r.label}</td>
                  <td className="p-2 text-right">{r['1M']}</td>
                  <td className="p-2 text-right">{r['3M']}</td>
                  <td className="p-2 text-right">{r['6M']}</td>
                  <td className="p-2 text-right">{r['12M']}</td>
                  <td className="p-2 text-right font-semibold">{r.total}</td>
                  <td className="p-2 text-right text-emerald-700">{r.success}</td>
                  <td className="p-2 text-right text-red-600">{r.failed}</td>
                  <td className="p-2 text-right">{RS(r.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Per-plan totals */}
      {data.plans?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">By Plan</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-600">
                <tr>
                  <th className="text-left p-2">Plan</th>
                  <th className="text-left p-2">Plan ID</th>
                  <th className="text-left p-2">Duration</th>
                  <th className="text-right p-2">Activations</th>
                  <th className="text-right p-2">Success</th>
                  <th className="text-right p-2">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {data.plans.map((p) => (
                  <tr key={p.plan_id} className="border-t hover:bg-slate-50">
                    <td className="p-2">{p.plan_name}</td>
                    <td className="p-2 font-mono text-xs">{p.plan_id}</td>
                    <td className="p-2"><Badge variant="outline">{DURATION_LABEL[p.duration] || p.duration}</Badge></td>
                    <td className="p-2 text-right">{p.count}</td>
                    <td className="p-2 text-right text-emerald-700">{p.success}</td>
                    <td className="p-2 text-right">{RS(p.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, tone = 'slate', testId }) {
  const tones = {
    slate: 'text-slate-700',
    emerald: 'text-emerald-700',
    red: 'text-red-600',
    indigo: 'text-indigo-700',
  };
  return (
    <Card>
      <CardContent className="py-3">
        <p className="text-[10px] text-slate-500 uppercase">{label}</p>
        <p className={`text-2xl font-bold ${tones[tone]}`} data-testid={testId}>{value}</p>
      </CardContent>
    </Card>
  );
}
